import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getApprovalDetail, submitApprovalAction } from "../services/api";

interface LineData {
    line_id: number;
    entry_date: string;
    hours: number;
    comments: string;
    line_status: string;
    rejection_reason: string | null;
}

interface TaskGroup {
    task_id: number;
    task_name: string;
    lines: LineData[];
}

interface TimesheetEntry {
    timesheet_id: number;
    period_start: string;
    period_end: string;
    submission_count: number;
    under_hours_warning: boolean;
    tasks: TaskGroup[];
}

interface ConsultantEntry {
    user_id: number;
    display_name: string;
    timesheets: TimesheetEntry[];
}

interface DetailResponse {
    project_id: number;
    project_name: string;
    consultants: ConsultantEntry[];
}

interface RejectionState {
    checked: boolean;
    reason: string;
}

const formatDate = (iso: string): string => {
    const d = new Date(iso + "T00:00:00");
    if (isNaN(d.getTime())) return iso;
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    return `${days[d.getDay()]} ${d.getMonth() + 1}/${d.getDate()}`;
};

const sectionHeaderStyle: React.CSSProperties = {
    display: "flex", alignItems: "center", gap: "8px",
    padding: "10px 14px", backgroundColor: "#5C5D5E", color: "white",
    borderRadius: "4px", cursor: "pointer", fontWeight: "bold", fontSize: "14px",
    marginBottom: "6px",
};

const taskHeaderStyle: React.CSSProperties = {
    display: "flex", alignItems: "center", gap: "8px",
    padding: "8px 14px", backgroundColor: "#F18E00", color: "white",
    borderRadius: "4px", cursor: "pointer", fontWeight: "bold", fontSize: "13px",
    marginBottom: "6px", marginLeft: "16px",
};

const lineRowStyle: React.CSSProperties = {
    display: "flex", alignItems: "center", gap: "14px",
    padding: "8px 14px", marginLeft: "32px", marginBottom: "4px",
    fontSize: "13px", color: "#333", borderRadius: "4px",
};

const btnStyle: React.CSSProperties = {
    backgroundColor: "#F18E00", color: "white", border: "none", borderRadius: "4px",
    padding: "10px 24px", cursor: "pointer", fontSize: "14px", fontWeight: "bold",
};

const ApprovalDetail: React.FC = () => {
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();
    const location = useLocation();

    // 2026-08-04 (v31): which queue this screen was entered from -- read
    // once from route state set by ApprovalQueue.tsx's row click, and
    // threaded through every navigate-back and API call below so an admin
    // reviewing someone else's approval stays in the admin queue/view
    // instead of bouncing back to their own personal "pm" queue.
    const mode: "pm" | "admin" = ((location.state as any)?.mode as "pm" | "admin") || "pm";

    const [data, setData] = useState<DetailResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
    const [rejections, setRejections] = useState<Record<number, RejectionState>>({});

    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!projectId) return;
        setLoading(true);
        setLoadError(null);
        try {
            const result = await getApprovalDetail(Number(projectId), mode);
            setData(result);
        } catch (e) {
            console.error("Failed to load approval detail", e);
            setLoadError("Failed to load this project's approvals. Please try again.");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, mode]);

    useEffect(() => {
        load();
    }, [load]);

    const toggleCollapsed = (key: string) => {
        setCollapsed(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const setRejectionChecked = (lineId: number, checked: boolean) => {
        setRejections(prev => ({
            ...prev,
            [lineId]: { checked, reason: prev[lineId]?.reason || "" },
        }));
    };

    const setRejectionReason = (lineId: number, reason: string) => {
        setRejections(prev => ({
            ...prev,
            [lineId]: { checked: prev[lineId]?.checked ?? true, reason },
        }));
    };

    const handleApprove = async () => {
        if (!data || !projectId) return;
        setSubmitError(null);

        // Validate every checked rejection has a reason of at least 2 characters
        for (const consultant of data.consultants) {
            for (const timesheet of consultant.timesheets) {
                for (const task of timesheet.tasks) {
                    for (const line of task.lines) {
                        const r = rejections[line.line_id];
                        if (r?.checked && r.reason.trim().length < 2) {
                            setSubmitError(
                                `Rejection reason for ${consultant.display_name}'s ${formatDate(line.entry_date)} entry needs at least 2 characters.`
                            );
                            return;
                        }
                    }
                }
            }
        }

        setSubmitting(true);

        // The action endpoint processes one timesheet at a time, and a
        // project's approval view can span multiple consultants/timesheets.
        // Submit each timesheet independently -- one timesheet's request
        // failing must not silently skip every timesheet after it in the
        // list (the old single try/catch around the whole loop did exactly
        // that: whatever ran before the failure had already committed on
        // the backend, but anything after it never even got submitted, all
        // while the user only saw one generic error with no indication of
        // which timesheets actually went through). Track failures instead
        // and report them specifically.
        const failures: string[] = [];
        for (const consultant of data.consultants) {
            for (const timesheet of consultant.timesheets) {
                const pendingLineIds = new Set<number>();
                timesheet.tasks.forEach(t =>
                    t.lines.forEach(l => {
                        if (l.line_status === "pending") pendingLineIds.add(l.line_id);
                    })
                );

                const timesheetRejections = Object.entries(rejections)
                    .filter(([lineId, r]) => r.checked && pendingLineIds.has(Number(lineId)))
                    .map(([lineId, r]) => ({ line_id: Number(lineId), reason: r.reason.trim() }));

                try {
                    await submitApprovalAction(Number(projectId), {
                        timesheet_id: timesheet.timesheet_id,
                        rejections: timesheetRejections,
                    }, mode);
                } catch (e: any) {
                    const detail = e?.response?.data?.detail || "unknown error";
                    failures.push(
                        `${consultant.display_name} (${formatDate(timesheet.period_start)} - ${formatDate(timesheet.period_end)}): ${detail}`
                    );
                }
            }
        }

        setSubmitting(false);

        if (failures.length === 0) {
            // Navigate with the success message in route state, not local
            // component state -- this component unmounts the instant
            // navigate() fires, so a message set here would never render.
            // ApprovalQueue.tsx shows it as a banner instead. This is the
            // same fix as the Timesheet Entry submit flow: previously a
            // successful approval/rejection just silently returned to the
            // queue with no confirmation shown at all.
            navigate("/approvals", { state: { flashMessage: "Approval decisions submitted successfully.", mode } });
        } else {
            // Some timesheets may well have succeeded despite the ones
            // listed below failing -- reload so the screen reflects what
            // actually happened (successful ones will have dropped out of
            // the pending view) rather than showing stale pre-submit state.
            setSubmitError(
                `${failures.length} timesheet(s) failed to submit and still need action:\n` +
                failures.join("\n")
            );
            await load();
        }
    };

    if (loading) return <div>Loading...</div>;

    if (loadError) {
        return (
            <div>
                <div style={{ color: "#C00000", fontSize: "14px", marginBottom: "12px" }}>{loadError}</div>
                <button onClick={load} style={btnStyle}>Retry</button>
            </div>
        );
    }

    if (!data) return null;

    return (
        <div>
            <button
                onClick={() => navigate("/approvals", { state: { mode } })}
                style={{ background: "none", border: "none", color: "#F18E00", cursor: "pointer", fontSize: "13px", padding: 0, marginBottom: "10px" }}
            >
                ← Back to Queue
            </button>
            <h1 style={{ color: "#5C5D5E", margin: "0 0 16px 0", fontSize: "20px" }}>
                Project: {data.project_name}
            </h1>

            {data.consultants.length === 0 && (
                <div style={{ color: "#999", fontSize: "14px" }}>No pending approvals for this project.</div>
            )}

            {data.consultants.map(consultant => {
                const consultantKey = `c-${consultant.user_id}`;
                const consultantCollapsed = collapsed.has(consultantKey);
                return (
                    <div key={consultant.user_id} style={{ marginBottom: "18px" }}>
                        <div style={sectionHeaderStyle} onClick={() => toggleCollapsed(consultantKey)}>
                            <span>{consultantCollapsed ? "▶" : "▼"}</span>
                            <span>{consultant.display_name}</span>
                        </div>
                        {!consultantCollapsed && consultant.timesheets.map(timesheet => (
                            <div key={timesheet.timesheet_id}>
                                {timesheet.tasks.map(task => {
                                    const taskKey = `t-${timesheet.timesheet_id}-${task.task_id}`;
                                    const taskCollapsed = collapsed.has(taskKey);
                                    return (
                                        <div key={task.task_id}>
                                            <div style={taskHeaderStyle} onClick={() => toggleCollapsed(taskKey)}>
                                                <span>{taskCollapsed ? "▶" : "▼"}</span>
                                                <span>{task.task_name}</span>
                                            </div>
                                            {!taskCollapsed && task.lines.map(line => {
                                                const isApproved = line.line_status === "approved";
                                                const isRejected = line.line_status === "rejected";
                                                const r = rejections[line.line_id];
                                                const bg = isRejected ? "#FCE4D6" : isApproved ? "#f5f5f5" : "white";
                                                return (
                                                    <div key={line.line_id}>
                                                        <div style={{
                                                            ...lineRowStyle, backgroundColor: bg,
                                                            color: isApproved ? "#999" : "#333",
                                                            border: "1px solid #eee",
                                                        }}>
                                                            <span style={{ width: "70px" }}>
                                                                {isApproved && "✓ "}{formatDate(line.entry_date)}
                                                            </span>
                                                            <span style={{ width: "40px" }}>{line.hours.toFixed(2)}</span>
                                                            <span style={{ flex: 1 }}>{line.comments}</span>
                                                            {line.line_status === "pending" && (
                                                                <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={!!r?.checked}
                                                                        onChange={e => setRejectionChecked(line.line_id, e.target.checked)}
                                                                        disabled={submitting}
                                                                    />
                                                                    Reject
                                                                </label>
                                                            )}
                                                            {isRejected && (
                                                                <span style={{ color: "#C00000", fontSize: "12px" }}>
                                                                    Rejected: {line.rejection_reason}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {line.line_status === "pending" && r?.checked && (
                                                            <div style={{ marginLeft: "32px", marginBottom: "8px" }}>
                                                                <textarea
                                                                    value={r.reason}
                                                                    onChange={e => setRejectionReason(line.line_id, e.target.value)}
                                                                    placeholder="Rejection reason required - min 2 chars"
                                                                    disabled={submitting}
                                                                    rows={2}
                                                                    style={{
                                                                        width: "100%", maxWidth: "500px", padding: "6px 8px",
                                                                        fontSize: "13px",
                                                                        border: r.reason.trim().length > 0 && r.reason.trim().length < 2
                                                                            ? "1px solid #C00000" : "1px solid #ddd",
                                                                        borderRadius: "4px", boxSizing: "border-box",
                                                                    }}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                );
            })}

            {data.consultants.length > 0 && (
                <div style={{ marginTop: "20px", borderTop: "1px solid #e0e0e0", paddingTop: "16px" }}>
                    {submitError && (
                        <div style={{ color: "#C00000", fontSize: "13px", marginBottom: "10px", whiteSpace: "pre-line" }}>{submitError}</div>
                    )}
                    <div style={{ textAlign: "right" }}>
                        <button
                            onClick={handleApprove}
                            disabled={submitting}
                            style={{ ...btnStyle, opacity: submitting ? 0.6 : 1, cursor: submitting ? "not-allowed" : "pointer" }}
                        >
                            {submitting ? "Submitting…" : "Approve / Reject"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ApprovalDetail;
